import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/core/components/ui/button';
import { ChevronDown, Filter, Eye, EyeOff } from 'lucide-react';

export const ALLOCATED_RANGES = [
  { id: '0-80', label: '0-80%' },
  { id: '80-100', label: '80-100%' },
  { id: '100+', label: '100%+' }
] as const;

export type AllocatedRange = typeof ALLOCATED_RANGES[number]['id'];

interface AllocatedCurrentFilterProps {
  selectedRanges: string[];
  onSelectedRangesChange: (ranges: string[]) => void;
}

export const AllocatedCurrentFilter = ({
  selectedRanges,
  onSelectedRangesChange
}: AllocatedCurrentFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.right - 256, // 256px = w-64 (dropdown width)
        width: rect.width
      });
    }
  }, [isOpen]);

  const selectedCount = selectedRanges.length;
  const hasActiveFilter = selectedCount > 0 && selectedCount < ALLOCATED_RANGES.length;

  const toggleRange = (rangeId: string) => {
    const newRanges = selectedRanges.includes(rangeId)
      ? selectedRanges.filter(r => r !== rangeId)
      : [...selectedRanges, rangeId];
    onSelectedRangesChange(newRanges);
  };

  const showAllRanges = () => {
    onSelectedRangesChange(ALLOCATED_RANGES.map(r => r.id));
  };

  const hideAllRanges = () => {
    onSelectedRangesChange([]);
  };

  return (
    <div ref={buttonRef} className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 text-sm ${
          hasActiveFilter ? 'bg-yellow-100 border-yellow-400 hover:bg-yellow-200' : ''
        }`}
      >
        <Filter className={`h-4 w-4 ${hasActiveFilter ? 'text-yellow-600' : ''}`} />
        <span>Allocated ({selectedCount}/{ALLOCATED_RANGES.length})</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div 
            className="fixed w-64 bg-white border rounded-lg shadow-lg z-50"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`
            }}
          >
            <div className="p-3 border-b">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm">Utilization %</span>
                <span className="text-xs text-gray-500">
                  {selectedCount} of {ALLOCATED_RANGES.length} selected
                </span>
              </div>
              
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={showAllRanges}
                  className="text-xs flex-1"
                >
                  Show All
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={hideAllRanges}
                  className="text-xs flex-1"
                >
                  Hide All
                </Button>
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {ALLOCATED_RANGES.map((range) => {
                const isSelected = selectedRanges.includes(range.id);
                
                return (
                  <div
                    key={range.id}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleRange(range.id)}
                  >
                    <div className="flex items-center space-x-2">
                      {isSelected ? (
                        <Eye className="h-4 w-4 text-blue-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      )}
                      <span className={`text-sm ${isSelected ? 'text-gray-900' : 'text-gray-500'}`}>
                        {range.label}
                      </span>
                    </div>
                    
                    <div className={`w-4 h-4 rounded border-2 ${
                      isSelected 
                        ? 'bg-blue-600 border-blue-600' 
                        : 'border-gray-300'
                    } flex items-center justify-center`}>
                      {isSelected && (
                        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 8 8">
                          <path d="M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z"/>
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
